import { AbstractConnector } from "@web3-react/abstract-connector"
import { useWeb3React } from "@web3-react/core"
import { useCallback, useEffect, useMemo, useReducer, useState } from "react"
import { useLocalStorage, usePrevious } from "react-use"
import { createContainer } from "unstated-next"

import { ChainId } from "../../../constant/network"
import { activateInjectedProvider } from "./../utils"
import { SUPPORTED_WALLETS, WalletType } from "../constant"
import { IS_PROD } from "../dependency"

enum ACTIONS {
    LOGIN_REQUEST = "LOGIN_REQUEST",
    LOGIN_SUCCESS = "LOGIN_SUCCESS",
    LOGIN_FAIL = "LOGIN_FAIL",
    LOGOUT = "LOGOUT",
}

type ActionType =
    | { type: ACTIONS.LOGIN_REQUEST }
    | { type: ACTIONS.LOGIN_SUCCESS; payload: { address: string } }
    | { type: ACTIONS.LOGIN_FAIL }
    | { type: ACTIONS.LOGOUT }

const initialState = {
    isLoading: false,
    address: "",
}

export const Account = createContainer(useAccount)

export const CONNECTOR_ID = {
    name: "connector-id",
    defaultValue: "",
}

function usePostLogout(onLogout: Function) {
    const { active } = useWeb3React()
    const previousSession = usePrevious(active)

    useEffect(() => {
        // NOTE: clear up state when user disconnect account
        if (previousSession && !active) {
            onLogout()
        }
    }, [previousSession, active, onLogout])
}

const addressFromURL = new URLSearchParams(window.location.search).get("address")
export type LoginProps = (instance: AbstractConnector, connectorId: string, onActivate?: Function) => void
function useAccount() {
    const { active, account: accountFromWeb3, activate, deactivate, chainId: accountChainId, library } = useWeb3React()

    // NOTE: This is for insiders to test addresses without connecting wallet
    const account = useMemo(() => {
        return addressFromURL || accountFromWeb3
    }, [accountFromWeb3])

    const [state, dispatch] = useReducer(reducer, initialState)
    const [, /* ConnectorId */ setConnectorId] = useLocalStorage(CONNECTOR_ID.name, CONNECTOR_ID.defaultValue)

    // NOTE: this will be called after user disconnect account
    usePostLogout(() => {
        setConnectorId("")
        dispatch({ type: ACTIONS.LOGOUT })
    })

    const login = useCallback(
        (instance: AbstractConnector, connectorId: string, onActivate?: Function) => {
            dispatch({ type: ACTIONS.LOGIN_REQUEST })
            setConnectorId(connectorId)
            activate(
                instance,
                err => {
                    console.error(err)
                },
                true,
            )
                .then(() => {
                    if (onActivate) {
                        onActivate()
                    }
                })
                .catch(err => {
                    setConnectorId("")
                    dispatch({ type: ACTIONS.LOGIN_FAIL })
                })
        },
        [dispatch, setConnectorId, activate],
    )

    const logout = useCallback(() => {
        deactivate()
    }, [deactivate])

    // NOTE: wait for login info
    useEffect(() => {
        if (active && account && accountChainId) {
            dispatch({ type: ACTIONS.LOGIN_SUCCESS, payload: { address: account } })
        }
    }, [active, account, accountChainId])

    // NOTE: auto login
    const [isTried, setIsTried] = useState(false)
    const [connectorId] = useLocalStorage(CONNECTOR_ID.name, CONNECTOR_ID.defaultValue)
    useEffect(() => {
        const connector = SUPPORTED_WALLETS.find(walletInfo => walletInfo.type === connectorId)?.connector
        if (!isTried && connector && connectorId) {
            if (connectorId === WalletType.METAMASK || WalletType.WALLETLINK_CONNECT) {
                activateInjectedProvider(connectorId as WalletType)
            }
            login(connector, connectorId)
            setIsTried(true)
        }
    }, [connectorId, isTried, login])

    const signer = useMemo(() => {
        const signer = library?.getSigner() || null
        return signer

        // NOTE: Put account as deps here because library and signer wouldn't be updated when user switch account
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [library, account])

    const isWrongNetwork = useMemo(() => {
        return IS_PROD
            ? accountChainId !== ChainId.OPTIMISTIC_ETHEREUM
            : accountChainId !== ChainId.OPTIMISTIC_ETHEREUM_TESTNET_KOVAN
    }, [accountChainId])

    return {
        isLoading: state.isLoading,
        active,
        web3Provider: library,
        signer,
        account: account || null,
        address: account?.toLowerCase() || null,
        isTesting: !!addressFromURL,
        accountChainId,
        isWrongNetwork,
        // NOTE: expose when useful later, or consider deprecate in the future.
        // state,
        actions: {
            login,
            logout,
        },
    }
}

function reducer(state: typeof initialState, action: ActionType) {
    switch (action.type) {
        case ACTIONS.LOGIN_REQUEST: {
            return { ...state, isLoading: true }
        }
        case ACTIONS.LOGIN_SUCCESS: {
            const { address } = action.payload
            return { ...state, isLoading: false, address }
        }
        case ACTIONS.LOGIN_FAIL: {
            return {
                ...state,
                isLoading: false,
            }
        }
        case ACTIONS.LOGOUT: {
            return {
                ...state,
                address: "",
            }
        }
        default:
            throw new Error()
    }
}
